import { motion } from "framer-motion";
import { BadgeCheck, MapPinned, Phone } from "lucide-react";
import { BRAND } from "@/content/site";
import { useLang } from "@/lang";

const STEP_ICONS = [Phone, MapPinned, BadgeCheck];
const EASE = [0.22, 1, 0.36, 1];

export default function HowItWorks() {
  const { t } = useLang();
  const vw = { once: true, amount: 0.3 };

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="relative overflow-hidden bg-navy py-20 sm:py-28 lg:py-32">
      <span aria-hidden="true" className="pointer-events-none absolute -right-10 top-6 select-none font-serif text-[18rem] italic leading-none text-cream/[0.03]">
        EC
      </span>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={vw} transition={{ duration: 0.8, ease: EASE }} className="mb-6 flex items-center gap-4">
            <span className="h-px w-12 bg-gold" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold">{t.how.eyebrow}</p>
          </motion.div>
          <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={vw} transition={{ duration: 0.8, ease: EASE, delay: 0.1 }} className="font-serif text-4xl font-medium leading-[1.1] text-cream sm:text-5xl">
            {t.how.title}
          </motion.h2>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-12 md:grid-cols-3 md:gap-10">
          {t.how.steps.map((s, i) => {
            const Icon = STEP_ICONS[i];
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={vw}
                transition={{ duration: 0.8, ease: EASE, delay: i * 0.15 }}
                data-testid={`step-card-${i + 1}`}
                className="relative border-t border-cream/15 pt-8"
              >
                <div className="flex items-start justify-between">
                  <span className="font-serif text-6xl italic leading-none text-gold/35">0{i + 1}</span>
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 text-gold">
                    <Icon className="h-[18px] w-[18px]" />
                  </span>
                </div>
                <h3 className="mt-6 font-serif text-2xl font-semibold text-cream">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-cream/70">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={vw} transition={{ duration: 0.8, ease: EASE }} className="mt-20 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-cream/50">{t.how.hotline}</p>
          <a href={BRAND.phoneHref} data-testid="how-hotline-link" className="mt-3 inline-block font-serif text-3xl italic text-gold transition-colors duration-300 hover:text-cream sm:text-4xl">
            {BRAND.phone}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
